import { KeyedObject } from "types/root";
import i18n from "../../i18n";
import useAuth from "hooks/useAuth";
import { useTranslation } from "react-i18next";
import { doPostReq } from "./data";
import { API_URLS } from "common/constants";
import { CountryFlag } from "common/components/CountryFlag";
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';


export function changeTranslationLanguage(lang: string) {
    i18n.changeLanguage(lang);
}

export function initLanguage() {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      changeTranslationLanguage(savedLanguage)
    } else {
      changeTranslationLanguage("en")
    }    
}

export function generateRandomString(length: number) {
  return Math.random().toString(36).substring(2, length + 2);
}

export const saveResponseMessagesToSS = (status: KeyedObject) => {
  try {
    sessionStorage.setItem("responseMessage", JSON.stringify(status))
  } catch (error) {}
}

export const deleteResponseMessagesFromSS = () => {
  sessionStorage.removeItem('responseMessage');
}

export const getResponseMessagesFromSS = () => {
  let status = null;
  try {
    status = JSON.parse(sessionStorage.getItem('responseMessage') || '')
  } catch (error) {
    status = null;
  }
  // We save status in session once - so delete after
  deleteResponseMessagesFromSS();
  return status;
}

export const getResponseMessages = (res: KeyedObject) => {
    const msgArr: KeyedObject[] = [];

    if (res.message) {
      msgArr.push(
        {
          "type": res.success ? "success" : "error",
          "key": "message",
          "value": res.message,
       }
      )
    }

    if (res?.errors) {

        if (Array.isArray(res?.errors)) {

          res.errors.forEach((obj: KeyedObject) => {
            Object.keys(obj).forEach((key: string) => {
                const val = obj[key];
                msgArr.push(
                  {
                    "type": "error",
                    "key": key,
                    "value": val,
                  }
                )
            })              
          })          

        }
        else if (typeof res?.errors === 'object') {

          Object.keys(res.errors).forEach((key: string) => {
              const val = res.errors[key];

              msgArr.push(
                {
                  "type": "error",
                  "key": key,
                  "value": val,
                }
              )
          })

        }

    }

    return msgArr;

}

export function attachErrorsToObj(errObj: KeyedObject, errors: KeyedObject[]) {
  errors.forEach(item => {
    if (item.type == "error") {
      errObj[item.key] = item.value;
    }
  })
  return errObj;
}

export function isObjInArrayObjects(arr: KeyedObject[], objKey: string, objVal: any) {
  for (let i = 0; i < arr.length; i++) {
    let obj = arr[i];

    if (obj[objKey] == objVal) {
      return true;
    }

    if (compareGuids(objVal, obj[objKey])) {
      return true;
    }

  }

  return false;
}

export const getDynamicUrl = (routeUrl: string, params: KeyedObject): string => {

  const finalUrlArr: string[] = []

  // Remove leading and trailing slashes
  let url = routeUrl.replace(/\/+$/, "");
  url = url.replace(/^\/+/, "");

  const urlArr = url.split("/")

  urlArr.forEach((u: string) => {
    if (u.startsWith(":")) {
      // This is a dynamic field
      const paramKey = u.replace(/^:+/, "");
      if (params[paramKey]) {
        finalUrlArr.push(params[paramKey])
      }
    } else {
      finalUrlArr.push(u)
    }
  })

  url = `/${finalUrlArr.join("/")}/`;

  return url;
}

export function cleanGuid(guid: string) {
  return guid?.replaceAll("-", "")
}

export function getProjectAppGuidViaAppGuid(appGuid: string, projectGuid: string | undefined, apps: KeyedObject[] | undefined): string {

  if (!projectGuid || !apps) return "";

  for (let i = 0; i < apps.length; i += 1) {
    const app: KeyedObject = apps[i];
    if (app.project_guid && (cleanGuid(projectGuid) == cleanGuid(app.project_guid))) {
      if (cleanGuid(appGuid) == cleanGuid(app.app_guid)){
        return app.project_app_guid;
      }
    }
  }
  return ""    
}

export const getProjectAppGuidViaAppName = (appName: string, projectGuid: string | undefined, apps: KeyedObject[] | undefined): string => {
  if (!projectGuid || !apps) return "";
  if (apps?.length) {
    for (let i = 0; i < apps.length; i += 1) {
      const app: KeyedObject = apps[i];
      if (app.name.toLocaleLowerCase() == appName.toLocaleLowerCase()){
        return getProjectAppGuidViaAppGuid(app.app_guid, projectGuid || "", apps || []); ;
      }
    }
  }
  
  return "";
}

export const getImageUrl = (url: string | undefined | null) => {
    if (!url) return "";
    if (
      url.toLowerCase().startsWith('http://') ||
      url.toLowerCase().startsWith('https://')
    ) {
      return url;
    }

    const auth = JSON.parse(window.localStorage.getItem('auth') || '');

    return `https://${auth?.session?.meta?.s3_prefix}/${url}`;
}

export function truncate(str: string, maxLength: number) {
  if (str.length > maxLength) {
    return str.substring(0, maxLength - 1) + '…'; 
  }
  return str;
}

export const getMemberItemData = (key: string, item: KeyedObject) => {
  const { t } = useTranslation();

  if (key == "name") {
    if (item.display_name){
      return item.display_name;
    }
    if (item.user_name){
      return item.user_name;
    }
    if (item.email){
      return item.email;
    }
  }
  if (key == "role") {
    return item.role
  }
  if (key == "status") {
    if (item.status == "active") {
      return "member"
    }
    return item.status
  }
  if (key == "groupAndRole") {
    return `${t(item.group_short_name)} ${item.role ? "(" + item.role + ")" : ''}`
  }
  if (key == "chipColor") {
    if (item.status == "active") {
      return "success"
    }
    return "warning"
  }
}


export function validateEmail(email: string) {
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return regex.test(email);
}

export const canBuyApps = (resDataProject: KeyedObject, resDataMembers: KeyedObject) => {
  const { user } = useAuth();

  const groupNames: any[] = [];

  if (resDataMembers && resDataMembers.data) {
    resDataMembers.data.forEach((member: KeyedObject) => {
      groupNames.push(member.group_short_name)
    })
  }

  if (resDataMembers && resDataMembers.group_info) {
    for (let i = 0; i < Object.keys(resDataMembers.group_info).length; i += 1) {
      const key = Object.keys(resDataMembers.group_info)[i];

      for (let x = 0; x < groupNames.length; x += 1) {
        const groupName = groupNames[x];

        if (groupName == key) {
          const permissions = resDataMembers.group_info[groupName].permissions;
          if (permissions && permissions.apps && permissions.apps.can_buy)
          {
            return true;
          }
        }
      }
    }
  }

  if (resDataProject && user?.guid == resDataProject.created_by_guid) {
    return true;
  }  
  
  return false;
}

export const canEditProject = (resDataProject: KeyedObject, resDataMembers: KeyedObject) => {

  const { user } = useAuth();

  const groupNames: any[] = [];

  if (resDataMembers && resDataMembers.data) {
    resDataMembers.data.forEach((member: KeyedObject) => {
      groupNames.push(member.group_short_name)
    })
  }

  if (resDataMembers && resDataMembers.group_info) {
    for (let i = 0; i < Object.keys(resDataMembers.group_info).length; i += 1) {
      const key = Object.keys(resDataMembers.group_info)[i];

      for (let x = 0; x < groupNames.length; x += 1) {
        const groupName = groupNames[x];

        if (groupName == key) {
          const permissions = resDataMembers.group_info[groupName].permissions;
          if (permissions && permissions.project && permissions.project.can_edit)
          {
            return true;
          }
        }
      }
    }
  }
  
  if (resDataProject && user?.guid == resDataProject.created_by_guid) {
    return true;
  }  

  return false;
}

export const isAdmin = (resDataProject: KeyedObject, resDataMembers: KeyedObject) => {

  const { user } = useAuth();

  const groupNames: any[] = [];

  if (resDataMembers && resDataMembers.data) {
    resDataMembers.data.forEach((member: KeyedObject) => {
      groupNames.push(member.group_short_name)
    })
    for (let i = 0; i < Object.keys(resDataMembers.group_info).length; i += 1) {
      const key = Object.keys(resDataMembers.group_info)[i];

      for (let x = 0; x < groupNames.length; x += 1) {
        const groupName = groupNames[x];

        if (groupName == key) {
          const permissions = resDataMembers.group_info[groupName].permissions;
          if (permissions && permissions.project && permissions.project.is_admin)
          {
            return true;
          }
        }
      }
    }
  }

  if (resDataProject && user?.guid == resDataProject.created_by_guid) {
    return true;
  }

  return false;
}

export const getMemberUserGuids = (resDataProject: KeyedObject, resDataMembers: KeyedObject) => {

  const guids = []

  if (resDataProject) {
    guids.push({
      guid: resDataProject.created_by_guid,
      user_name: resDataProject.created_by_user_name  
    })
  }

  if (resDataMembers && resDataMembers.data) {
    resDataMembers.data.forEach((member: KeyedObject) => {
      if (member.user_guid && member.status == "active") {
        guids.push({
          guid: member.user_guid,
          user_name: member.user_name,
        })
      }
    })
  }

  return guids;
}

// TODO - when we introduce multiple currencies then use this
// export const getCurrency = (currencyGuid: string) => {
export const getCurrency = () => {
  const { user } = useAuth();
  return user?.economy?.currency;
}

export const compareGuids = (guid1: string, guid2: string) => {
  return guid1?.replaceAll("-", "") == guid2?.replaceAll("-", "");;
}

export const getSession = () => {
  const auth = JSON.parse(window.localStorage.getItem('auth') || '');
  return auth.session;
}

export const getProjectPermissions = (projectGuid: string, pageName?: string) => {

  const perms: KeyedObject = {
    isAdmin: false,
    isOwner: false,
    isFollower: false,
    isTeamChat: false,
    canPost: false,
    followerGroupId: 0,
    teamChatGroupId: 0,
  };

  const session = getSession();

  const projects = session.projects;

  projects?.ownerships.forEach( (item: KeyedObject) => {
    if (compareGuids(item.project_guid, projectGuid)) {
      perms.isAdmin = true;
      perms.isOwner = true;
    }

  })  

  projects?.groups.forEach( (item: KeyedObject) => {
    if (compareGuids(item.project_guid, projectGuid)) {
      if (item.group_short_name == "project_followers") {
        perms.isFollower = true;
        perms.followerGroupId = item.group_id;
      }
      if (item.group_short_name == "project_team") {
        perms.isTeamChat = true;
        perms.teamChatGroupId = item.group_id;
      }      
    }
  })


  if (pageName) {
    if (pageName == "TabFollowerChat" && perms.isFollower) {
      perms.canPost = true;
    }
    if (pageName == "TabTeamChat" && perms.isTeamChat) {
      perms.canPost = true;
    }
  }

  return perms;
}

export const getMergedProjects = () => {
  const following = getSessionProjects("following")

  const memberships = getSessionProjects("memberships")
  
  const invitations = getSessionProjects("invitations")

  const finalList = memberships;

  // Step 1 add invitations to project with same guid
  for (let x = 0; x < finalList.length; x++) {
    const finalItem = finalList[x]

    finalItem.following = []

    for (let y = 0; y < invitations.length; y++) {
      const invitation = invitations[y];

      if (invitation.guid == finalItem.guid) {
        finalItem.invitations = invitation.invitations;
      }
    }
  }

  // Step 2 add following to project with same guid
  for (let x = 0; x < finalList.length; x++) {
    const finalItem = finalList[x]

    for (let y = 0; y < following.length; y++) {
      const fObj = following[y];

      if (compareGuids(fObj.guid, finalItem.guid)) {
        finalItem.following.push(
          fObj
        );
      }
    }
  }

  // Step 3 - add invitations of other project
  for (let y = 0; y < invitations.length; y++) {
    const invitation = invitations[y];

    if (!isObjInArrayObjects(finalList, "guid", invitation.guid)){
      finalList.push(invitation)
    }
  }

  // Step 4 - remove following info if it has already been addded
  const following2 = []

  for (let y = 0; y < following.length; y++) {
    const fObj = following[y];

    if (!isObjInArrayObjects(finalList, "guid", fObj.guid)){
      following2.push(fObj)
    }
  }

  return {
    following: following2,
    finalList: finalList
  }

}

export const getTeamChatGroupId = (dataType: string, item: KeyedObject) => {

  // NOTE: Team chat is in groups but not in memberships
  // so add to memberships 

  if (dataType != "memberships") return;

  if (!item.is_team_chat) return;

  const session = getSession();

  const projects = session.projects;

  const groups = projects?.groups;

  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];
    if (compareGuids(item.project_guid, group.project_guid)) {
      if (group.group_short_name == "project_team") {
        return group.group_id;
      }
    }
  }
}


export const getSessionProjects = (dataType: string) => {

  const listItems: KeyedObject[] = [];

  const session = getSession();

  const projects = session.projects;

  if (dataType == "admin") {

    const admins = [
      "project_admins",
      "project_sysadmins",
    ]

    projects?.groups.forEach( (item: KeyedObject) => {
      if (admins.includes(item.group_short_name)) {
        if (!isObjInArrayObjects(listItems, "guid", item.project_guid)){
            listItems.push(
              {
                "id": item.project_id,
                "guid": item.project_guid,
                "name": item.project_name,
                "status": item.project_status,
                "privacy": item.project_privacy,
              }
            )
        }
      }
    })

    projects?.ownerships.forEach( (item: KeyedObject) => {
      if (!isObjInArrayObjects(listItems, "guid", item.project_guid)){
        listItems.push(
          {
            "id": item.project_id,
            "guid": item.project_guid,
            "name": item.project_name,
            "status": item.project_status,
            "privacy": item.project_privacy,
          }
        )
      }
    })      
  }
  else if (dataType == "following") {

    projects?.groups.forEach( (item: KeyedObject) => {
      if (item.group_short_name == "project_followers") {
        if (!isObjInArrayObjects(listItems, "guid", item.project_guid)){
            listItems.push(
              {
                "guid": item.project_guid,
                "name": item.project_name,
                "follower_chat_group_id": item.group_id,
              }
            )
        }
      }
    })    
    
  }

  else if ( ["invitations", "memberships"].includes(dataType) && projects[dataType]) {

    projects[dataType].forEach( (item: KeyedObject) => {
      if (!isObjInArrayObjects(listItems, "guid", item.project_guid)){

        const obj: KeyedObject = {
            "id": item.project_id,
            "guid": item.project_guid,
            "name": item.project_name,
            "status": item.project_status,
            "privacy": item.project_privacy,
        }

        obj[dataType] = [];

        listItems.push(
          obj
        )
      }
    })      

    projects[dataType].forEach( (item: KeyedObject) => {

      for (let i = 0; i < listItems.length; i++) {
        const listItem = listItems[i]

        if (listItem.guid == item.project_guid) {

          if (!isObjInArrayObjects(listItem[dataType], "guid", item.guid)){

            listItem[dataType].push(
              {
                guid: item.guid,
                member_guid: item.member_guid,
                group_short_name: item.group_short_name,
                invited_at: item.invited_at,
                role: item.role,
                is_team_chat: item.is_team_chat,
                team_chat_group_id: getTeamChatGroupId(dataType, item),
                invitation_status: item.invitation_status,
                invitation_accepted_at: item.invitation_accepted_at,
              }
            )
          }
        }
      }

    }) 

  }


  return listItems;

}

export const followOrUnfollowProject = (action: string, projectGuid: string, callback: any) => {

  const params = {
    "interaction": "follow",
    "action": action == "follow" ? "add" : "remove",
  }

  doPostReq({
    url: getDynamicUrl(API_URLS.followProject, {project_guid: projectGuid}),
    callback: callback,
    params: params
  })
}

export const flattenMsgs = (t: any, obj: any) => {

  const errArr = []

  if (obj.message) {
    errArr.push(t(obj.message))
  }

  if (obj.errors) {
    if (obj.errors.__all__) {
      obj.errors.__all__.forEach((item: string) => {
        errArr.push(t(item))
      })
    }
  }

  return errArr.join(", ")

}

export const capitalizeWords = (str: string) => {
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}


export const getCountryFlagDisplay = (type: string, demographies: KeyedObject) => {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (type == "physical") {
    if (demographies?.project_physical_location_country?.iso_code) {
      return <>
        <Stack sx={{ mr: 0.5, fontSize: { xs: 13 } }}>
          <CountryFlag 
            countryCode={demographies?.project_physical_location_country?.iso_code} 
            className={`${isMobile ? 'shims-w-23' : 'shims-w-30'} shims-border-1`}
          />
        </Stack>
        <Stack sx={{ mr: 0.5, fontSize: { xs: 13, md: 15, lg: 15 } }}>
          {demographies?.project_physical_location_country?.iso_code.toUpperCase()}
        </Stack>
        { demographies?.project_physical_location_country?.iso_code.toUpperCase() == "US" && (
          <Stack sx={{ mr: 0.5, fontSize: { xs: 13, md: 15, lg: 15  } }}>
            &gt; {demographies?.project_physical_location_state?.name} &nbsp;

          </Stack>
        )}
        { demographies?.project_physical_location_country?.iso_code.toUpperCase() == "CA" && (
          <Stack sx={{ mr: 0.5, fontSize: { xs: 13, md: 15, lg: 15  } }}>
            &gt; {demographies?.project_physical_location_province?.name}
          </Stack>
        )}

        { demographies?.project_physical_location_town?.name && (
          <Stack sx={{ mr: 0.5, fontSize: { xs: 13, md: 15, lg: 15  } }}>
            &gt; {demographies?.project_physical_location_town?.name}
          </Stack>
        )}
      </>            
    }
  }
  else if (type == "about") {
    if (demographies?.project_about_location_country?.iso_code && (demographies?.project_about_location_country?.iso_code != demographies?.project_physical_location_country?.iso_code)) {
      return <>
        <Stack sx={{ mr: 0.5, fontSize: { xs: 13, md: 15, lg: 15  }  }}>
        |
        </Stack>
        <Stack sx={{ mr: 0.5, fontSize: { xs: 13, md: 15, lg: 15  }  }}>
          <CountryFlag 
            countryCode={demographies?.project_about_location_country?.iso_code} 
            className={`${isMobile ? 'shims-w-23' : 'shims-w-30'} shims-border-1`}
          />

        </Stack>
        <Stack sx={{ mr: 0.5, fontSize: { xs: 13, md: 15, lg: 15  }  }}>
          {demographies?.project_about_location_country?.iso_code.toUpperCase()}
        </Stack>
        { demographies?.project_about_location_country?.iso_code.toUpperCase() == "US" && demographies?.project_about_location_state?.iso_code && (
        <Stack sx={{ mr: 0.5, fontSize: { xs: 13, md: 15, lg: 15  }  }}>
            &gt; {demographies?.project_about_location_state?.name}

          </Stack>
        )}
        { demographies?.project_about_location_country?.iso_code.toUpperCase() == "CA" && demographies?.project_about_location_province?.iso_code && (
        <Stack sx={{ mr: 0.5, fontSize: { xs: 13, md: 15, lg: 15  } }}>
            &gt; {demographies?.project_about_location_province?.name}

          </Stack>
        )}

        { demographies?.project_about_location_town?.name && (
        <Stack sx={{ mr: 0.5, fontSize: { xs: 13, md: 15, lg: 15  }  }}>
            &gt; {demographies?.project_about_location_town?.name}

          </Stack>
        )}
      </>            
    }
  }

  return <></>



}